import {
  Check,
  Cloud,
  Download,
  FileIcon,
  HardDrive,
  LockKeyhole,
  LoaderCircle,
  LogOut,
  Search,
  Trash2,
  UploadCloud,
  X,
} from "lucide-react";
import { type FormEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { DiskFile, FilesResponse, Retention } from "../shared/contracts";
import {
  ApiError,
  cancelUpload,
  completeUpload,
  createUpload,
  deleteFile,
  getDownloadUrl,
  getFiles,
  getSession,
  login,
  logout,
  uploadToR2,
} from "./api";
import { fileKind, formatBytes, formatDate, retentionLabels } from "./format";

type QueueStatus = "queued" | "uploading" | "done" | "error";
interface QueueItem {
  id: string;
  file: File;
  retention: Retention;
  status: QueueStatus;
  progress: number;
  error?: string;
  uploadId?: string;
}

const emptyData: FilesResponse = {
  files: [],
  usage: { usedBytes: 0, pendingBytes: 0, quotaBytes: 9_000_000_000, maxFileBytes: 5 * 1024 ** 3 },
};

function getErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : "发生未知错误";
}

export default function App() {
  const [data, setData] = useState<FilesResponse>(emptyData);
  const [email, setEmail] = useState("");
  const [retention, setRetention] = useState<Retention>("7d");
  const [query, setQuery] = useState("");
  const [queue, setQueue] = useState<QueueItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [authenticated, setAuthenticated] = useState<boolean | null>(null);
  const [password, setPassword] = useState("");
  const [loginBusy, setLoginBusy] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const [deletingKey, setDeletingKey] = useState<string | null>(null);
  const [downloadingKey, setDownloadingKey] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const activeRef = useRef<{ queueId: string; uploadId?: string; abort?: () => void } | null>(null);

  const refresh = useCallback(async () => {
    const next = await getFiles();
    setData(next);
  }, []);

  useEffect(() => {
    Promise.all([getSession(), getFiles()])
      .then(([session, files]) => {
        setEmail(session.email);
        setData(files);
        setAuthenticated(true);
      })
      .catch((error) => {
        if (error instanceof ApiError && error.status === 401) setAuthenticated(false);
        else setNotice(getErrorMessage(error));
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const next = queue.find((item) => item.status === "queued");
    if (!next || activeRef.current) return;

    activeRef.current = { queueId: next.id };
    setQueue((items) => items.map((item) => (item.id === next.id ? { ...item, status: "uploading" } : item)));

    void (async () => {
      try {
        const ticket = await createUpload(next.file, next.retention);
        activeRef.current = { ...activeRef.current!, uploadId: ticket.uploadId };
        setQueue((items) => items.map((item) => (item.id === next.id ? { ...item, uploadId: ticket.uploadId } : item)));
        const transfer = uploadToR2(ticket, next.file, (progress) => {
          setQueue((items) => items.map((item) => (item.id === next.id ? { ...item, progress } : item)));
        });
        activeRef.current = { ...activeRef.current!, abort: transfer.abort };
        await transfer.promise;
        await completeUpload(ticket.uploadId);
        setQueue((items) => items.map((item) => (item.id === next.id ? { ...item, status: "done", progress: 100 } : item)));
        await refresh();
      } catch (error) {
        const aborted = error instanceof ApiError && error.code === "UPLOAD_ABORTED";
        setQueue((items) =>
          items.map((item) =>
            item.id === next.id
              ? { ...item, status: "error", error: aborted ? "已取消" : getErrorMessage(error) }
              : item,
          ),
        );
        if (!aborted) setNotice(getErrorMessage(error));
      } finally {
        activeRef.current = null;
        setQueue((items) => [...items]);
      }
    })();
  }, [queue, refresh]);

  const addFiles = useCallback(
    (files: FileList | File[]) => {
      const additions = Array.from(files).map<QueueItem>((file) => ({
        id: crypto.randomUUID(),
        file,
        retention,
        status: "queued",
        progress: 0,
      }));
      setQueue((current) => [...current.filter((item) => item.status !== "done"), ...additions]);
      setNotice(null);
    },
    [retention],
  );

  const cancelItem = useCallback(async (item: QueueItem) => {
    if (activeRef.current?.queueId === item.id) activeRef.current.abort?.();
    const uploadId = item.uploadId ?? (activeRef.current?.queueId === item.id ? activeRef.current.uploadId : undefined);
    if (uploadId) {
      try {
        await cancelUpload(uploadId);
      } catch {
        // The reservation expires automatically if cancellation cannot reach the API.
      }
    }
    setQueue((items) => items.filter((candidate) => candidate.id !== item.id));
  }, []);

  const filteredFiles = useMemo(() => {
    const normalized = query.trim().toLocaleLowerCase();
    return normalized ? data.files.filter((file) => file.name.toLocaleLowerCase().includes(normalized)) : data.files;
  }, [data.files, query]);

  const usedWithPending = data.usage.usedBytes + data.usage.pendingBytes;
  const usagePercent = Math.min(100, (usedWithPending / data.usage.quotaBytes) * 100);

  const handleDownload = async (file: DiskFile) => {
    setDownloadingKey(file.key);
    try {
      const url = await getDownloadUrl(file.key);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.rel = "noopener";
      document.body.append(anchor);
      anchor.click();
      anchor.remove();
    } catch (error) {
      setNotice(getErrorMessage(error));
    } finally {
      setDownloadingKey(null);
    }
  };

  const handleDelete = async (file: DiskFile) => {
    if (!window.confirm(`永久删除“${file.name}”？此操作无法撤销。`)) return;
    setDeletingKey(file.key);
    try {
      await deleteFile(file.key);
      setData((current) => ({
        ...current,
        files: current.files.filter((item) => item.key !== file.key),
        usage: { ...current.usage, usedBytes: Math.max(0, current.usage.usedBytes - file.size) },
      }));
    } catch (error) {
      setNotice(getErrorMessage(error));
    } finally {
      setDeletingKey(null);
    }
  };

  const handleLogin = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!password) return;
    setLoginBusy(true);
    setLoginError(null);
    try {
      const session = await login(password);
      const files = await getFiles();
      setEmail(session.email);
      setData(files);
      setPassword("");
      setAuthenticated(true);
    } catch (error) {
      setLoginError(getErrorMessage(error));
    } finally {
      setLoginBusy(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
    } finally {
      activeRef.current?.abort?.();
      activeRef.current = null;
      setQueue([]);
      setData(emptyData);
      setAuthenticated(false);
      setEmail("");
    }
  };

  if (authenticated === false) {
    return (
      <main className="login-page">
        <section className="login-card" aria-labelledby="login-title">
          <span className="login-mark"><LockKeyhole size={24} /></span>
          <p className="eyebrow">PRIVATE STORAGE</p>
          <h1 id="login-title">你的私人云盘</h1>
          <p>输入密码后即可访问文件。密码仅用于登录验证，不会上传到 R2。</p>
          <form onSubmit={(event) => void handleLogin(event)}>
            <label>
              <span>登录密码</span>
              <input
                autoFocus
                autoComplete="current-password"
                disabled={loginBusy}
                onChange={(event) => setPassword(event.target.value)}
                placeholder="输入密码"
                type="password"
                value={password}
              />
            </label>
            {loginError && <p className="login-error" role="alert">{loginError}</p>}
            <button className="login-button" disabled={loginBusy || !password} type="submit">
              {loginBusy ? "正在验证…" : "进入云盘"}
            </button>
          </form>
        </section>
      </main>
    );
  }

  if (authenticated === null) {
    return <main className="login-page"><div className="loading-screen"><LoaderCircle className="spin" size={24} />正在加载…</div></main>;
  }

  return (
    <div className="app-shell">
      <header className="topbar">
        <div className="brand">
          <span className="brand-mark"><Cloud size={21} strokeWidth={1.8} /></span>
          <span><strong>Sweet Winds</strong><small>私人云盘</small></span>
        </div>
        <div className="account">
          <span className="status-dot" />
          <span className="account-email">{email || "正在验证…"}</span>
          <button className="icon-button" onClick={() => void handleLogout()} aria-label="退出登录" title="退出登录">
            <LogOut size={17} />
          </button>
        </div>
      </header>

      <main>
        <section className="intro">
          <div>
            <p className="eyebrow">PRIVATE STORAGE</p>
            <h1>文件暂存，<br />清爽而安心。</h1>
            <p className="intro-copy">文件从浏览器直达私有存储。到期自动清理，不公开，不打扰。</p>
          </div>
          <aside className="capacity-card">
            <div className="capacity-heading"><HardDrive size={19} /><span>存储空间</span><strong>{usagePercent.toFixed(0)}%</strong></div>
            <div className="capacity-track"><span style={{ width: `${usagePercent}%` }} /></div>
            <div className="capacity-meta"><span>已用 {formatBytes(data.usage.usedBytes)}</span><span>上限 {formatBytes(data.usage.quotaBytes)}</span></div>
            {data.usage.pendingBytes > 0 && <p>另有 {formatBytes(data.usage.pendingBytes)} 正在上传</p>}
          </aside>
        </section>

        {notice && <div className="notice" role="alert"><span>{notice}</span><button onClick={() => setNotice(null)} aria-label="关闭提示"><X size={16} /></button></div>}

        <section className="upload-panel">
          <div
            className={`drop-zone ${dragging ? "is-dragging" : ""}`}
            onDragEnter={(event) => { event.preventDefault(); setDragging(true); }}
            onDragOver={(event) => event.preventDefault()}
            onDragLeave={(event) => { if (event.currentTarget === event.target) setDragging(false); }}
            onDrop={(event) => { event.preventDefault(); setDragging(false); addFiles(event.dataTransfer.files); }}
            onClick={() => inputRef.current?.click()}
            onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") inputRef.current?.click(); }}
            role="button"
            tabIndex={0}
          >
            <input ref={inputRef} type="file" multiple hidden onChange={(event) => event.target.files && addFiles(event.target.files)} />
            <span className="upload-icon"><UploadCloud size={27} /></span>
            <div><strong>把文件拖到这里</strong><span>或点击选择文件 · 单文件最大 5 GiB</span></div>
          </div>
          <div className="retention-control">
            <span>保存期限</span>
            <div className="segmented" aria-label="保存期限">
              {(Object.keys(retentionLabels) as Retention[]).map((value) => (
                <button key={value} className={retention === value ? "active" : ""} onClick={() => setRetention(value)}>
                  {retentionLabels[value]}
                </button>
              ))}
            </div>
          </div>
        </section>

        {queue.length > 0 && (
          <section className="queue" aria-label="上传队列">
            {queue.map((item) => (
              <div className="queue-item" key={item.id}>
                <span className={`queue-state ${item.status}`}>
                  {item.status === "done" ? <Check size={16} /> : item.status === "uploading" ? <LoaderCircle className="spin" size={16} /> : <FileIcon size={16} />}
                </span>
                <div className="queue-info"><strong title={item.file.name}>{item.file.name}</strong><span>{item.error ?? `${formatBytes(item.file.size)} · ${retentionLabels[item.retention]}`}</span></div>
                <div className="queue-progress"><span style={{ width: `${item.progress}%` }} /></div>
                <span className="queue-percent">{item.status === "queued" ? "等待" : item.status === "error" ? "失败" : `${item.progress}%`}</span>
                <button className="icon-button" onClick={() => void cancelItem(item)} aria-label={`取消 ${item.file.name}`}><X size={16} /></button>
              </div>
            ))}
          </section>
        )}

        <section className="files-section">
          <div className="section-heading">
            <div><h2>我的文件</h2><span>{data.files.length} 个文件</span></div>
            <label className="search-box"><Search size={17} /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="搜索文件" aria-label="搜索文件" /></label>
          </div>

          {loading ? (
            <div className="empty-state"><LoaderCircle className="spin" size={24} /><p>正在读取文件…</p></div>
          ) : filteredFiles.length === 0 ? (
            <div className="empty-state"><span><Cloud size={28} /></span><h3>{query ? "没有匹配的文件" : "这里还很空"}</h3><p>{query ? "换个关键词试试" : "上传一个文件，开始使用你的私人空间。"}</p></div>
          ) : (
            <div className="file-list">
              <div className="file-row file-header"><span>名称</span><span>大小</span><span>上传时间</span><span>到期</span><span /></div>
              {filteredFiles.map((file) => (
                <article className="file-row" key={file.key}>
                  <div className="file-name"><span className="file-icon"><FileIcon size={19} /></span><span><strong title={file.name}>{file.name}</strong><small>{fileKind(file.contentType)}</small></span></div>
                  <span data-label="大小">{formatBytes(file.size)}</span>
                  <span data-label="上传">{formatDate(file.uploadedAt)}</span>
                  <span data-label="到期" className={file.retention === "keep" ? "keep" : "expiry"}>{file.expiresAt ? formatDate(file.expiresAt) : "永久保留"}</span>
                  <div className="file-actions">
                    <button className="icon-button" onClick={() => void handleDownload(file)} disabled={downloadingKey === file.key} aria-label={`下载 ${file.name}`} title="下载">
                      {downloadingKey === file.key ? <LoaderCircle className="spin" size={17} /> : <Download size={17} />}
                    </button>
                    <button className="icon-button danger" onClick={() => void handleDelete(file)} disabled={deletingKey === file.key} aria-label={`删除 ${file.name}`} title="永久删除">
                      {deletingKey === file.key ? <LoaderCircle className="spin" size={17} /> : <Trash2 size={17} />}
                    </button>
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>
      </main>

      <footer><span>Sweet Winds Cloud Disk</span><span>Private · Encrypted in transit · Auto-expiring</span></footer>
    </div>
  );
}
